'use client';

import { c as _c } from "react/compiler-runtime";
import { use, useEffect, useId, useMemo } from 'react';
import { useUnmount } from 'usehooks-ts';
import type { Command, CommandPullRequest } from './types';
import { CommandStoreContext } from './registry';

/**
 * A simplified hook to register commands in the global command context.
 *
 * For factory usage, command recomputation is controlled by explicit deps.
 * Keep deps complete so commands refresh when intended.
 *
 * Each registration re-renders the palette. A registrar the palette renders must not re-render
 * with it, so wrap it in `memo`: otherwise a dep that changes identity on every render, such as
 * a callback built from a `useMutation` result, registers on every palette render, and each
 * registration renders the palette again.
 *
 * @param commandsOrFactory - Either an array of Command objects or a factory function returning commands
 * @param deps - Optional array of dependencies if using a factory function
 */
export function useRegisterCommands(commandsOrFactory: Command[] | (() => Command[]), deps: unknown[] = []) {
  // Null outside a CommandProvider (the desktop tray window), where registering does nothing.
  const store = use(CommandStoreContext);
  const isFactory = typeof commandsOrFactory === 'function';

  // Generate a stable group ID for this component instance using React's useId
  const groupId = useId();

  // For factory usage, command regeneration is controlled by `deps` only.
  // We intentionally do not depend on the inline factory function identity.
  const commandsFromFactory = useMemo(() => {
    if (typeof commandsOrFactory === 'function') {
      return commandsOrFactory();
    }
    return null;
    // We intentionally ignore `commandsOrFactory` identity for factory mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFactory, ...deps]);
  const commandsFromArray = useMemo(() => {
    if (typeof commandsOrFactory === 'function') {
      return null;
    }
    return commandsOrFactory;
  }, [commandsOrFactory]);
  const commands = useMemo(() => {
    return isFactory ? commandsFromFactory ?? [] : commandsFromArray ?? [];
  }, [commandsFromArray, commandsFromFactory, isFactory]);

  // Register on mount or whenever commands changes
  useEffect(() => {
    store?.register(groupId, commands);
  }, [commands, groupId, store]);

  // Unregister only on unmount to avoid register/unregister churn during updates.
  useUnmount(() => {
    store?.unregister(groupId);
  });
}

/**
 * Tells the palette which pull request this component's commands act on, until it unmounts. The
 * last component to register wins. Pass a stable object: each new one notifies the palette.
 */
export function useRegisterPaletteTarget(target) {
  const $ = _c(8);
  const store = use(CommandStoreContext);
  const registrarId = useId();
  let t0;
  let t1;
  if ($[0] !== registrarId || $[1] !== store || $[2] !== target) {
    t0 = () => {
      store?.setTarget(registrarId, target);
    };
    t1 = [registrarId, store, target];
    $[0] = registrarId;
    $[1] = store;
    $[2] = target;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  if ($[5] !== registrarId || $[6] !== store) {
    t2 = () => {
      store?.setTarget(registrarId, null);
    };
    $[5] = registrarId;
    $[6] = store;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useUnmount(t2);
}